import React from 'react';
import { Bot, Send, Sparkles } from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { AI_ASSISTANT_SHORTCUT } from '@/components/layout/AccountingShellProvider';

export interface AccountingAiAssistantPanelProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

type ChatMessage = {
  id: string;
  role: 'user' | 'assistant';
  text: string;
};

const STARTER_PROMPTS = [
  'مانده یک حساب کل را چطور ببینم؟',
  'تفاوت حساب معین و تفصیلی چیست؟',
  'چطور سند حسابداری برای پروژه ثبت کنم؟',
];

const AccountingAiAssistantPanel: React.FC<AccountingAiAssistantPanelProps> = ({ open, onOpenChange }) => {
  const [input, setInput] = React.useState('');
  const [messages, setMessages] = React.useState<ChatMessage[]>([
    {
      id: 'welcome',
      role: 'assistant',
      text: 'سلام! من دستیار هوش مصنوعی حسابداری هستم. درباره ثبت سند، گزارش گردش حساب و پروژه‌ها می‌توانید از من بپرسید.',
    },
  ]);

  const send = (text: string) => {
    const trimmed = text.trim();
    if (!trimmed) return;

    const userMsg: ChatMessage = { id: `u-${Date.now()}`, role: 'user', text: trimmed };
    setMessages((prev) => [...prev, userMsg]);
    setInput('');

    window.setTimeout(() => {
      setMessages((prev) => [
        ...prev,
        {
          id: `a-${Date.now()}`,
          role: 'assistant',
          text: buildPlaceholderReply(trimmed),
        },
      ]);
    }, 400);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="flex h-[min(85vh,640px)] max-w-lg flex-col gap-0 p-0 [&>button]:left-4 [&>button]:right-auto">
        <DialogHeader className="border-b border-slate-200 px-5 py-4">
          <div className="flex items-center gap-3">
            <span className="inline-flex h-10 w-10 items-center justify-center rounded-full bg-violet-100 text-violet-700">
              <Sparkles className="h-5 w-5" />
            </span>
            <div className="text-right">
              <DialogTitle className="text-base">هوش مصنوعی</DialogTitle>
              <p className="mt-0.5 text-xs text-slate-500">میانبر: {AI_ASSISTANT_SHORTCUT}</p>
            </div>
          </div>
        </DialogHeader>

        <div className="min-h-0 flex-1 space-y-3 overflow-y-auto px-4 py-4">
          {messages.map((m) => (
            <div
              key={m.id}
              className={`flex ${m.role === 'user' ? 'justify-start' : 'justify-end'}`}
            >
              <div
                className={`max-w-[90%] rounded-2xl px-3 py-2 text-sm leading-6 ${
                  m.role === 'user'
                    ? 'bg-sky-600 text-white'
                    : 'border border-slate-200 bg-slate-50 text-slate-800'
                }`}
              >
                {m.role === 'assistant' ? (
                  <Bot className="mb-1 inline h-4 w-4 text-violet-600" />
                ) : null}{' '}
                {m.text}
              </div>
            </div>
          ))}
        </div>

        <div className="border-t border-slate-200 px-4 py-3">
          <div className="mb-2 flex flex-wrap gap-1.5">
            {STARTER_PROMPTS.map((p) => (
              <button
                key={p}
                type="button"
                onClick={() => send(p)}
                className="rounded-full border border-slate-200 bg-white px-2.5 py-1 text-xs text-slate-600 hover:border-violet-200 hover:bg-violet-50"
              >
                {p}
              </button>
            ))}
          </div>
          <form
            className="flex gap-2"
            onSubmit={(e) => {
              e.preventDefault();
              send(input);
            }}
          >
            <Input
              value={input}
              onChange={(e) => setInput(e.target.value)}
              placeholder="سؤال خود را بنویسید..."
              className="h-10"
            />
            <Button type="submit" size="icon" className="h-10 w-10 shrink-0" disabled={!input.trim()}>
              <Send className="h-4 w-4" />
            </Button>
          </form>
        </div>
      </DialogContent>
    </Dialog>
  );
};

function buildPlaceholderReply(question: string): string {
  const q = question.toLowerCase();
  if (q.includes('سرفصل') || q.includes('کل') || q.includes('معین')) {
    return 'ماژول سرفصل حساب‌ها فعلاً حذف شده و در حال بازنویسی است. کد حساب را در سند به‌صورت دستی وارد کنید.';
  }
  if (q.includes('سند') || q.includes('ثبت')) {
    return 'ثبت سند از مسیر «اسناد حسابداری» (/accounting-documents) انجام می‌شود. می‌توانید projectId را در آدرس بدهید تا پروژه از قبل انتخاب شود.';
  }
  if (q.includes('گردش') || q.includes('مانده')) {
    return 'گزارش گردش حساب در مسیر /accounting-reports/account-turnover است. کد حساب را دستی وارد کنید.';
  }
  return 'فعلاً در حالت راهنمای اولیه هستم. به‌زودی به API هوش مصنوعی متصل می‌شوم.';
}

export default AccountingAiAssistantPanel;
